import { Injectable } from '@angular/core';

@Injectable({
    providedIn: 'root'
})
export class FcpMenuService {
    private menuItems: FcpMenuItem[] = [];

    /**
     * Register menu items for the application.
     *
     * @param items Array of menu items to register
     *
     * @example
     * ```typescript
     * this.menuService.registerItems([
     *   {
     *     label: 'File',
     *     items: [
     *       { label: 'New', icon: 'pi pi-plus', command: () => this.createNew() },
     *       { label: 'Open', icon: 'pi pi-folder-open', command: () => this.open() }
     *     ]
     *   }
     * ]);
     * ```
     */
    registerItems(items: FcpMenuItem[]): void {
        this.menuItems = [...items];
    }

    /**
     * Get all registered menu items.
     *
     * @returns Array of current menu items
     */
    getMenuItems(): FcpMenuItem[] {
        return [...this.menuItems];
    }

    /**
     * Clear all registered menu items.
     *
     * @example
     * ```typescript
     * ngOnDestroy() {
     *   this.menuService.clearMenuItems();
     * }
     * ```
     */
    clearMenuItems(): void {
        this.menuItems = [];
    }

    /**
     * Add a single menu item to the existing collection.
     *
     * @param item Menu item to add
     *
     * @example
     * ```typescript
     * this.menuService.addMenuItem({
     *   label: 'Settings',
     *   icon: 'pi pi-cog',
     *   routerLink: ['/settings']
     * });
     * ```
     */
    addMenuItem(item: FcpMenuItem): void {
        this.menuItems.push(item);
    }

    /**
     * Remove a menu item by its ID.
     *
     * @param id Unique identifier of the menu item to remove
     * @returns true if item was found and removed, false otherwise
     */
    removeMenuItemById(id: string): boolean {
        const initialLength = this.menuItems.length;
        this.menuItems = this.menuItems.filter((item) => item.id !== id);
        return this.menuItems.length < initialLength;
    }
}

export interface FcpMenuItem {
    id?: string;
    label?: string;
    icon?: string;
    url?: string;
    routerLink?: string | string[];
    command?: (event?: Event) => void;
    disabled?: boolean;
    visible?: boolean;
    class?: string;
    items?: FcpMenuItem[];
    target?: string;
    separator?: boolean;
    badge?: string;
    badgeClass?: string;
    title?: string;
}
